/**
 * Versions Table Component
 *
 * Displays file versions in a sortable, filterable table with tag management.
 * Provides actions for tagging, exporting, and deleting versions.
 *
 * Features:
 * - Tag management (system tags: current/previous, custom tags via workspace tags)
 * - Export to Excel functionality
 * - Protected version deletion (prevents deleting current/previous)
 * - System tag filtering and display
 * - SHA badge for version identification
 */

import React, { useMemo, useState, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { Files, Calendar } from 'lucide-react';
import { Button, Badge } from '@loader/ui';
import type { VersionExportFormat } from '@loader/shared-types';

import { VersionActionMenu } from './VersionActionMenu';
import { VersionTagsCell } from './VersionTagsCell';
import { VersionFileNameCell } from './VersionFileNameCell';

import {
  DataTable,
  useDataGrid,
  createNameColumn,
  createTextColumn,
  createNumberColumn,
  createDateColumn,
} from '@/components/data-grid';
import { SHABadge, EmptyView, ConfirmDeleteDialog } from '@/components/common';
import EditVersionTagsDialog from '@/features/tables/components/dialogs/EditVersionTagsDialog';
import { formatDate } from '@/lib/formatters';
import { invalidateVersionQueries } from '@/lib/queryInvalidation';
import { useRpc } from '@/lib/rpc';
import { toast } from '@/lib/toast';
import { useTableBulkDownload, type DownloadFormat } from '@/features/tables/hooks/useBulkDownload';
import type { Version } from '@/features/tables/types';

/**
 * Type guard to check if a format is supported by bulk download
 * Bulk download supports: json, csv, xlsx, xml (not parquet)
 */
function isBulkDownloadFormat(format: VersionExportFormat): format is DownloadFormat {
  return format === 'json' || format === 'csv' || format === 'xlsx' || format === 'xml';
}

/**
 * Query parameters for listing versions
 */
interface VersionsListQueryParams {
  /** Filter by table ID (UUID) */
  table_id?: string;
}

/**
 * Props for VersionsTable component
 */
interface VersionsTableProps {
  /** Table ID to filter versions (optional - if not provided, shows all versions) */
  tableId?: string;
  /** Callback when version row is clicked */
  onVersionClick?: (version: Version) => void;
  /** Callback when version is successfully deleted */
  onDeleteSuccess?: (versionId: string) => void;
  /** Callback when tags are updated */
  onTagsUpdated?: () => void;
  /** Visual variant for DataTable: 'card' (default) or 'flush' */
  variant?: 'card' | 'flush';
  /** Whether user can edit - viewers see read-only version list */
  canEdit?: boolean;
}

/**
 * VersionsTable Component
 *
 * Renders a data table with version information and action buttons.
 * Handles tag management, export, and deletion with proper validation.
 *
 * @param versions - Array of versions to display
 * @param onVersionClick - Version click handler
 * @param onDeleteSuccess - Delete success callback
 * @param onTagsUpdated - Tags updated callback
 */
export const VersionsTable: React.FC<VersionsTableProps> = ({
  tableId,
  onVersionClick,
  onDeleteSuccess,
  onTagsUpdated,
  variant = 'card',
  canEdit = true,
}) => {
  // Hooks
  const rpc = useRpc();
  const queryClient = useQueryClient();
  const { mutate: downloadTable } = useTableBulkDownload();

  // Server-side data grid with filtering, sorting, and pagination
  const grid = useDataGrid<Version, VersionsListQueryParams>({
    endpoint: 'versions',
    initialPageSize: 20,
    initialSortBy: 'created_at', // Use snake_case field name
    initialSortDir: 'desc',
    extraParams: tableId ? { table_id: tableId } : {},
  });

  // Dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Version | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<Version | null>(null);

  /**
   * Refresh version queries after mutation
   * Invalidates both data grid and version picker queries
   */
  const refreshVersions = useCallback(async () => {
    await invalidateVersionQueries(queryClient, tableId);
    grid.queryResult.refetch();
  }, [queryClient, tableId, grid.queryResult]);

  /**
   * Request version deletion (opens confirmation dialog)
   * Shows toast for protected/last version instead of dialog
   * Deletes immediately without confirmation for failed versions
   *
   * @param version - Version to delete
   * @param isProtected - Whether version has a system tag (current/previous)
   * @param isOnlyVersion - Whether this is the only version
   */
  const handleRequestDelete = useCallback(async (
    version: Version,
    isProtected: boolean,
    isOnlyVersion: boolean
  ) => {
    if (isOnlyVersion) {
      toast.version.deleteLast();
      return;
    }
    if (isProtected) {
      toast.version.deleteProtected();
      return;
    }
    // Failed versions can be deleted without confirmation
    if (version.status === 'failed') {
      try {
        // @ts-expect-error - Hono RPC type inference limitation for nested routes
        const res = await rpc.api.private.versions[':id'].$delete({
          param: { id: version.id },
        });
        if (!res.ok) throw new Error('Failed to delete version');
        toast.version.deleted(version.file_name);
        onDeleteSuccess?.(version.id);
        await refreshVersions();
      } catch (error) {
        toast.version.deleteFailed(error instanceof Error ? error.message : undefined);
      }
      return;
    }
    setPendingDelete(version);
    setConfirmOpen(true);
  }, [rpc, onDeleteSuccess, refreshVersions]);

  /**
   * Confirm and execute version deletion
   */
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      setIsDeleting(true);
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.versions[':id'].$delete({
        param: { id: pendingDelete.id },
      });

      if (!res.ok) {
        throw new Error('Failed to delete version');
      }

      toast.version.deleted(pendingDelete.file_name);
      onDeleteSuccess?.(pendingDelete.id);
      setConfirmOpen(false);
      setPendingDelete(null);
      await refreshVersions();
    } catch (error) {
      // Check for 409 dependency conflict
      if (
        error instanceof Error &&
        'status' in error &&
        (error as Error & { status: number; dependentViews?: string[] })
          .status === 409
      ) {
        const dependentViews = (
          error as Error & { status: number; dependentViews?: string[] }
        ).dependentViews;
        if (dependentViews && dependentViews.length > 0) {
          toast.version.deleteBlocked(dependentViews);
        } else {
          toast.version.deleteFailed(
            error instanceof Error ? error.message : undefined
          );
        }
      } else {
        toast.version.deleteFailed(
          error instanceof Error ? error.message : undefined
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Open edit tags dialog
   *
   * @param version - Version to edit tags for
   */
  const handleOpenEdit = useCallback((version: Version) => {
    setPendingEdit(version);
    setEditOpen(true);
  }, []);

  /**
   * Export version to specified format
   *
   * Uses bulk download via signed tokens for large datasets.
   * Supports: json, csv, xlsx, xml
   *
   * @param version - Version to export
   * @param format - Export format (json, csv, xlsx, parquet)
   */
  const handleExport = useCallback(
    async (version: Version, format: VersionExportFormat) => {
      // Bulk download supports: json, csv, xlsx, xml
      // For parquet, fall back to the old export method
      if (format === 'parquet') {
        try {
          // @ts-expect-error - Hono RPC type inference limitation for nested routes
          const res = await rpc.api.private.versions[':id'].export.$get({
            param: { id: version.id },
            query: { format },
          });

          if (!res.ok) {
            throw new Error('Failed to export version');
          }

          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${version.file_name}_${new Date().toISOString().split('T')[0]}.${format}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          // Download started automatically via browser - no toast needed
        } catch (error) {
          toast.version.exportFailed(
            error instanceof Error ? error.message : undefined
          );
        }
        return;
      }

      // Use bulk download for json, csv, xlsx, xml (bypasses CF Worker limits)
      // Type guard ensures format is valid for bulk download
      if (isBulkDownloadFormat(format)) {
        downloadTable({
          tableId: version.table_id,
          versionId: version.id,
          format,
        });
      }
    },
    [rpc, downloadTable]
  );

  /**
   * Define table columns with custom renderers and actions
   * Memoized to prevent unnecessary re-renders
   */
  const columnDefs = useMemo((): ColumnDef<Version>[] => {
    const columns: ColumnDef<Version>[] = [
      createNameColumn<Version>('file_name', 'File', {
        icon: Files,
        minWidth: 250,
        maxWidth: 400,
        cellRenderer: (_value, original) => {
          const version = original as Version;
          return <VersionFileNameCell version={version} />;
        },
        filterFn: 'includesString',
      }),
      createNumberColumn<Version>('version_number', 'Version', {
        minWidth: 80,
        maxWidth: 100,
        cellRenderer: (value) => {
          const versionNum = value as number;
          return (
            <span className="font-mono font-medium text-muted-foreground">
              v{versionNum}
            </span>
          );
        },
      }),
      // SHA (first 8 chars of version UUID), placed right after the name
      createTextColumn<Version>('id', 'SHA', {
        minWidth: 100,
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterFn: 'includesString',
        cellRenderer: (_value, original) => (
          <SHABadge id={(original as Version).id} />
        ),
      }),
      createTextColumn<Version>('status', 'Status', {
        minWidth: 100,
        maxWidth: 120,
        cellRenderer: (value) => {
          const status = String(value);
          const variant =
            status === 'completed'
              ? 'success'
              : status === 'processing'
                ? 'info'
                : status === 'pending'
                  ? 'warning'
                  : 'destructive';
          return <Badge variant={variant}>{status}</Badge>;
        },
      }),
      createNumberColumn<Version>('records_processed', 'Records', {
        cellRenderer: (value) => {
          const records = value as number;
          return records ? records.toLocaleString() : '-';
        },
        filterFn: 'basic',
      }),
      createDateColumn<Version>('created_at', 'Versioned At', {
        cellRenderer: (value) => {
          return (
            <div className="flex items-center gap-2">
              <Calendar className="text-muted-foreground h-4 w-4" />
              <span>{formatDate(value as string)}</span>
            </div>
          );
        },
      }),
      createTextColumn<Version>('tags', 'Tags', {
        cellRenderer: (_value, original) => {
          const version = original as Version;
          return <VersionTagsCell system_tag={version.system_tag} tags={version.tags} />;
        },
        filterFn: 'includesString',
      }),
    ];

    // Only include actions column for editors/owners
    if (canEdit) {
      columns.push({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const version = row.original as Version;
          return (
            <VersionActionMenu
              version={version}
              tableId={tableId ?? version.table_id ?? ''}
              isOnlyVersion={grid.pagination.total === 1}
              onTagsUpdated={() => {
                grid.queryResult.refetch();
                onTagsUpdated?.();
              }}
              onOpenEdit={handleOpenEdit}
              onRequestDelete={handleRequestDelete}
              onExport={handleExport}
            />
          );
        },
      });
    }

    return columns;
  }, [
    tableId,
    onTagsUpdated,
    handleExport,
    handleOpenEdit,
    handleRequestDelete,
    grid.queryResult,
    grid.pagination.total,
    canEdit,
  ]);

  return (
    <div className="h-full w-full">
      <DataTable<Version>
        data={grid.data as Version[]}
        columns={columnDefs}
        isLoading={grid.isLoading}
        error={grid.error}
        isFetching={grid.isFetching}
        // Server-side pagination
        pageIndex={grid.pagination.pageIndex}
        pageSize={grid.pagination.pageSize}
        totalItems={grid.pagination.total}
        pageCount={grid.pagination.pageCount}
        onPageChange={grid.actions.setPage}
        onPageSizeChange={grid.actions.setPageSize}
        // Server-side sorting
        sortBy={grid.sorting.sort_by}
        sortDir={grid.sorting.sort_dir}
        onSortChange={grid.actions.setSort}
        // Server-side filtering
        filters={grid.filters}
        onFilterChange={grid.actions.setFilter}
        onFilterRemove={grid.actions.removeFilter}
        onFiltersClear={grid.actions.clearFilters}
        // Features
        enableFiltering={true}
        enablePagination={true}
        showFooter={true}
        hideVerticalLines={true}
        pageSizeOptions={[10, 20, 50, 100]}
        onRowClick={onVersionClick}
        variant={variant}
        emptyStateComponent={
          <EmptyView
            title="No versions found"
            description="Versions track changes to your table over time. Upload a file to create your first version."
            icon={Files}
          >
            {tableId && canEdit && (
              <Button asChild>
                <Link to="/versions/new" search={{ tableId }}>Create your first version</Link>
              </Button>
            )}
          </EmptyView>
        }
      />
      {pendingDelete && (
        <ConfirmDeleteDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Delete Version"
          isLoading={isDeleting}
          onConfirm={handleConfirmDelete}
        />
      )}
      <EditVersionTagsDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        version={pendingEdit}
        onSaved={() => {
          onTagsUpdated?.();
          // Refetch grid data after tags are updated
          grid.queryResult.refetch();
        }}
      />
    </div>
  );
};
